import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
  CrmSuppressionSourceViewDto,
  CrmSuppressionSourceCreatePayload,
  CrmPickerOptionsDto,
} from '@/types/crmSuppressionSources'

const base = (clientCompanyId: string) => `/api/ClientCompanies/${clientCompanyId}/Integrations`

export const crmSuppressionSourcesApi = {
  // Every source the company has configured, newest first.
  list: (clientCompanyId: string): Promise<ApiResponse<CrmSuppressionSourceViewDto[]>> =>
    apiClient.get(`${base(clientCompanyId)}/SuppressionSources`),

  // Add a source. 422 when it could never work — wrong provider for the kind, nothing connected, or a
  // field name the query builder would refuse.
  add: (clientCompanyId: string, payload: CrmSuppressionSourceCreatePayload): Promise<ApiResponse<CrmSuppressionSourceViewDto>> =>
    apiClient.post(`${base(clientCompanyId)}/SuppressionSources`, payload),

  // Deletes the pointer only. Addresses it already suppressed stay suppressed.
  remove: (clientCompanyId: string, sourceId: string): Promise<ApiResponse<null>> =>
    apiClient.delete(`${base(clientCompanyId)}/SuppressionSources/${sourceId}`),

  // Run-now. Returns 202 immediately — a large list is hundreds of upstream calls, so the outcome
  // shows up on the source's own last-synced fields once the worker has stamped them.
  sync: (clientCompanyId: string, sourceId: string): Promise<ApiResponse<null>> =>
    apiClient.post(`${base(clientCompanyId)}/SuppressionSources/${sourceId}/Sync`, {}),

  // Picker feeds. Each answers 200 with a status rather than erroring when the CRM refuses the read.
  // The status is what separates a refusal — which reconnecting fixes — from a CRM that genuinely has
  // nothing to offer, which it cannot.
  hubSpotLists: (clientCompanyId: string): Promise<ApiResponse<CrmPickerOptionsDto>> =>
    apiClient.get(`${base(clientCompanyId)}/HubSpot/Lists`),

  salesforceCampaigns: (clientCompanyId: string): Promise<ApiResponse<CrmPickerOptionsDto>> =>
    apiClient.get(`${base(clientCompanyId)}/Salesforce/Campaigns`),

  salesforceSuppressionFields: (clientCompanyId: string): Promise<ApiResponse<CrmPickerOptionsDto>> =>
    apiClient.get(`${base(clientCompanyId)}/Salesforce/SuppressionFields`),
}
